import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Subject, debounceTime, fromEvent, merge } from 'rxjs';
import { idleTimeoutConfig } from 'src/app/const/constant';

export enum IdleTimeoutReason {
  IDLE_EXPIRED = 'idle_expired',
  USER_LOGOUT = 'user_logout',
  INVALID_TOKEN = 'invalid_token',
  API_ERROR = 'api_error',
  SESSION_EXPIRED_ON_RESUME = 'session_expired_on_resume',
  MANUAL = 'manual',
}

/**
 * IdleTimeoutService - Manages user activity monitoring and idle timeout state
 * 
 * Responsibilities:
 * 1. Monitor user activity (mouse, keyboard, scroll, touch)
 * 2. Track idle state and emit events when user becomes idle
 * 3. Manage idle timer reset on user activity
 * 4. Provide observables for idle state changes
 */
@Injectable({
  providedIn: 'root',
})
export class IdleTimeoutService {
  // Configuration
  private readonly IDLE_TIME_MS = idleTimeoutConfig.idleTime * 60 * 1000; // Convert to ms
  private readonly ACTIVITY_DEBOUNCE_MS = idleTimeoutConfig.activityDebounce;

  // State management
  private lastActivity = new Date();
  private isIdle$ = new BehaviorSubject<boolean>(false);
  private activitySubject$ = new Subject<void>();
  private idleTimerId: NodeJS.Timeout | null = null;
  private isMonitoring = false; // Track if monitoring is active

  // Activity event listeners
  private activityEvents$ = merge(
    fromEvent(document, 'mousedown'),
    fromEvent(document, 'mousemove'),
    fromEvent(document, 'keypress'),
    fromEvent(document, 'scroll'),
    fromEvent(document, 'touchstart'),
    fromEvent(document, 'click')
  );

  constructor(private ngZone: NgZone) {
    this.initializeActivityMonitoring();
  }

  /**
   * Initialize activity monitoring with debounce
   * Runs outside of Angular zone for better performance
   */
  private initializeActivityMonitoring(): void {
    this.ngZone.runOutsideAngular(() => {
      this.activityEvents$
        .pipe(debounceTime(this.ACTIVITY_DEBOUNCE_MS))
        .subscribe(() => {
          this.ngZone.run(() => {
            this.onActivity();
          });
        });
    });
  }

  /**
   * Handle user activity - reset idle timer
   * CRITICAL: Reset timer on ANY activity when monitoring is active
   * This keeps the 60-minute counter fresh whenever the user interacts
   */
  private onActivity(): void {
    // Only process activity if monitoring is active
    if (!this.isMonitoring) {
      return;
    }

    // Always reset the timer on any activity when monitoring
    // This ensures the 60-minute idle counter is refreshed with each user interaction
    console.log('[IdleTimeout] Activity detected - resetting idle timer');
    this.resetIdleTimer();
  }

  /**
   * Start the idle timeout monitoring
   * Should be called once after user authentication
   */
  public startMonitoring(): void {
    console.log('[IdleTimeout] Starting activity monitoring');
    this.isMonitoring = true;
    this.lastActivity = new Date();
    this.resetIdleTimer();
  }

  /**
   * Stop the idle timeout monitoring
   * Should be called on logout
   */
  public stopMonitoring(): void {
    console.log('[IdleTimeout] Stopping activity monitoring');
    this.isMonitoring = false;
    this.clearIdleTimer();
    this.isIdle$.next(false);
  }

  /**
   * Reset the idle timer (called on user activity)
   */
  private resetIdleTimer(): void {
    this.lastActivity = new Date();
    this.clearIdleTimer();
    this.isIdle$.next(false);

    // Update last activity timestamp in sessionStorage for page resume detection
    sessionStorage.setItem('lastActivityTime', Date.now().toString());

    // Start new idle timer
    this.idleTimerId = setTimeout(() => {
      console.log('[IdleTimeout] Idle timeout reached - user has been inactive for', idleTimeoutConfig.idleTime, 'minutes');
      this.setIdle();
    }, this.IDLE_TIME_MS);
  }

  /**
   * Mark user as idle and emit idle state change
   */
  private setIdle(): void {
    console.log('[IdleTimeout] User is now idle');
    this.isIdle$.next(true);
    this.activitySubject$.next();
  }

  /**
   * Clear the idle timer
   */
  private clearIdleTimer(): void {
    if (this.idleTimerId) {
      clearTimeout(this.idleTimerId);
      this.idleTimerId = null;
    }
  }

  /**
   * Get the idle state observable
   * Emits true when user becomes idle (60 minutes without activity)
   */
  public getIdleState$() {
    return this.isIdle$.asObservable();
  }

  /**
   * Check if user is currently idle
   */
  public isUserIdle(): boolean {
    return this.isIdle$.value;
  }

  /**
   * Get last activity timestamp
   */
  public getLastActivity(): Date {
    return this.lastActivity;
  }

  /**
   * Get time (in ms) until idle timeout
   */
  public getTimeUntilIdle(): number {
    const elapsed = new Date().getTime() - this.lastActivity.getTime();
    return Math.max(0, this.IDLE_TIME_MS - elapsed);
  }

  /**
   * Manually trigger idle state (for testing or forced scenarios)
   */
  public forceIdle(): void {
    console.log('[IdleTimeout] Forcing idle state');
    this.clearIdleTimer();
    this.setIdle();
  }

  /**
   * Cleanup on service destruction
   */
  ngOnDestroy(): void {
    this.clearIdleTimer();
  }
}
